import { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useAuth } from "../../contexts/UserContext";

// Hooks
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useMetaDescription } from "../../hooks/useMetaDescription";
import toast from "react-hot-toast";
import { toastDefsError } from "../../config/toastConfig";
import { Oval } from "react-loader-spinner";
import { comum } from "../../styles/Themes/light";
import InputDefault from "../../components/form/InputDefault";
import ButtonDefault from "../../components/form/ButtonDefault";
import ChangePasswordApplicants from "./ChangePasswordApplicants";
import api from "../../services/api";

interface Applicant {
    id?: string;
    nome?: string;
    email?: string;
    telefone?: string;
    cpf?: string;
}

export default function ApplicantProfile() {
    const params = useParams<any>();
    const profileId = params.id;
    const history = useHistory();
    const { setPageName, user } = useAuth();

    // Define o título dinâmico baseado se é o próprio usuário ou não
    const isOwnProfile = user?.id?.toString() === profileId;
    const pageTitle = isOwnProfile ? "Meu Perfil" : "Perfil Solicitante";
    
    // Define o título e meta description da página
    useDocumentTitle({ title: pageTitle });
    useMetaDescription({ 
        description: isOwnProfile 
            ? "Gerencie suas informações pessoais e configurações de perfil de solicitante na EasyJur Legal Ops."
            : "Visualize as informações do solicitante na EasyJur Legal Ops."
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [profile, setProfile] = useState<Applicant>();
    const [showChangePassword, setShowChangePassword] = useState<boolean>(false);

    function redirectWithError(message: string) {
        history.replace('/dashboard', {
            toast: {
                type: 'error' as const,
                message,
            },
        });
    }

    useEffect(() => {
        if (user?.profile === 'Solicitante' && user?.id?.toString() !== profileId) {
            redirectWithError('Acesso negado.');
            return;
        }
        getProfile();
    }, [profileId, user, history]);

    async function getProfile() {
        try {
            setLoading(true);
            const response = await api.get(`solicitantes/${profileId}`);
            setProfile(response.data.result);
            
            if (response.data.result && response.data.result.nome) {
                setPageName(`${response.data.result.nome} - ${profileId}`);
            }
            
        } catch (error: any) {
            redirectWithError('Não foi possível carregar o perfil.');
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <Oval
                height={150}
                width={150}
                color={comum.colors.primaryRed400}
                wrapperStyle={{}}
                wrapperClass="loader"
                visible={true}
                ariaLabel='oval-loading'
                secondaryColor={comum.colors.primaryRed400}
                strokeWidth={2}
                strokeWidthSecondary={2}
            />
        );
    }

    // Função vazia para os inputs desabilitados
    const handleInputChange = () => {};

    // Função para formatar telefone dinamicamente
    const formatPhoneNumber = (phone: string) => {
        if (!phone) return '';
        
        // Remove todos os caracteres não numéricos
        const phoneNumbers = phone.replace(/\D/g, '');
        
        // Se tem 11 dígitos (celular): (99) 9 9999-9999
        if (phoneNumbers.length === 11) {
            return phoneNumbers.replace(/(\d{2})(\d{1})(\d{4})(\d{4})/, '($1) $2 $3-$4');
        }
        // Se tem 10 dígitos (fixo): (99) 9999-9999
        else if (phoneNumbers.length === 10) {
            return phoneNumbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        
        // Retorna o valor original se não conseguir formatar
        return phone;
    };

    return (
        <div>
            {profile && !showChangePassword && (
                <div>
                    <div className="flex justify-end mb-6">
                        <div className="" style={{width: '150px'}}>
                            <ButtonDefault
                                onClick={() => setShowChangePassword(true)}
                                className="access"
                            >
                                Alterar Senha
                            </ButtonDefault>
                        </div>
                    </div>

                    <div className="profile">
                        <form className="register-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <InputDefault
                                        title="Nome:"
                                        name="nome"
                                        value={profile.nome || ''}
                                        disabled={true}
                                        onChangeText={handleInputChange}
                                        className="big"
                                    />
                                </div>
                                <div>
                                    <InputDefault
                                        title="Email:"
                                        name="email"
                                        value={profile.email || ''}
                                        disabled={true}
                                        onChangeText={handleInputChange}
                                        className="big"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <InputDefault
                                        title="Telefone:"
                                        name="telefone"
                                        placeholder="(00) 00000-0000 ou (00) 0 0000-0000"
                                        value={formatPhoneNumber(profile.telefone || '')}
                                        disabled={true}
                                        onChangeText={handleInputChange}
                                        className="small"
                                    />
                                </div>
                                <div>
                                    <InputDefault
                                        title="CPF:"
                                        name="cpf"
                                        mask="999.999.999-99"
                                        placeholder="000.000.000-00"
                                        value={profile.cpf || ''}
                                        disabled={true}
                                        onChangeText={handleInputChange}
                                        className="small"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showChangePassword && (
                <div>
                    <ChangePasswordApplicants 
                        applicantId={profileId} 
                        onCancel={() => setShowChangePassword(false)} 
                    />
                </div>
            )}
        </div>
    );
} 